import React from "react";
import { Container } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react";
import { ArrowForward, FormatQuote } from "@material-ui/icons";
import styles from "./_css/platformBanner.module.css";
import { settingsStore } from "settings/_stores/settingsStore";

const ListingsIcon = () => (
    <svg width="38" height="38" viewBox="0 0 38 38" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path
            d="M5 17.5L19 6.5L33 17.5V31.5C33 32.0523 32.5523 32.5 32 32.5H24V22.5H14V32.5H6C5.44772 32.5 5 32.0523 5 31.5V17.5Z"
            stroke="white"
            strokeWidth="1.7"
            strokeLinejoin="round"
        />
    </svg>
);

const ProjectsIcon = () => (
    <svg width="38" height="38" viewBox="0 0 38 38" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M19 10H8V32H19V10Z" stroke="white" strokeWidth="1.7" strokeLinejoin="round" />
        <path d="M30 16H19V32H30V16Z" stroke="white" strokeWidth="1.7" strokeLinejoin="round" />
        <path d="M13.2 13.5H11V15.7H13.2V13.5Z" fill="#F5B840" />
        <path d="M17 13.5H14.8V15.7H17V13.5Z" fill="white" />
        <path d="M13.2 18H11V20.2H13.2V18Z" fill="white" />
        <path d="M17 18H14.8V20.2H17V18Z" fill="white" />
        <path d="M13.2 22.5H11V24.7H13.2V22.5Z" fill="white" />
        <path d="M17 22.5H14.8V24.7H17V22.5Z" fill="white" />
        <path d="M24 19H22V21H24V19Z" fill="white" />
        <path d="M27.5 19H25.5V21H27.5V19Z" fill="white" />
        <path d="M24 23H22V25H24V23Z" fill="white" />
        <path d="M27.5 23H25.5V25H27.5V23Z" fill="white" />
    </svg>
);

const CitiesIcon = () => (
    <svg width="38" height="38" viewBox="0 0 38 38" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path
            d="M19 33C19 33 29 24.5 29 16.5C29 11 24.5228 6.5 19 6.5C13.4772 6.5 9 11 9 16.5C9 24.5 19 33 19 33Z"
            stroke="white"
            strokeWidth="1.7"
            strokeLinejoin="round"
        />
        <path
            d="M19 19.4C20.7673 19.4 22.2 17.9673 22.2 16.2C22.2 14.4327 20.7673 13 19 13C17.2327 13 15.8 14.4327 15.8 16.2C15.8 17.9673 17.2327 19.4 19 19.4Z"
            fill="#F5B840"
        />
    </svg>
);

export const PlatformBanner = observer(() => {
    const { t, i18n } = useTranslation();

    // Le CTA ramène vers la barre de recherche du hero (ancre) et donne le focus au champ.
    const scrollToSearch = (event: React.MouseEvent) => {
        event.preventDefault();
        const section = document.getElementById("recherche");
        if (!section) return;
        section.scrollIntoView({ behavior: "smooth", block: "center" });
        section.querySelector("input")?.focus({ preventScroll: true });
    };

    // Chiffres synchronisés depuis le backend (via les settings préchargés en SSR).
    // Repli sur la valeur i18n si les settings ne sont pas encore disponibles.
    const counts = settingsStore.getOne()?.counts;
    const formatCount = (value: number | undefined, fallback: string) =>
        typeof value === "number" ? new Intl.NumberFormat(i18n.language).format(value) : fallback;

    const stats = [
        {
            Icon: ProjectsIcon,
            value: formatCount(counts?.onlineProjects, t("homePage.platformBanner.stats.projectsValue")),
            label: t("homePage.platformBanner.stats.projectsLabel"),
            sub: t("homePage.platformBanner.stats.projectsSub"),
        },
        {
            Icon: ListingsIcon,
            value: formatCount(counts?.activeListings, t("homePage.platformBanner.stats.listingsValue")),
            label: t("homePage.platformBanner.stats.listingsLabel"),
            sub: t("homePage.platformBanner.stats.listingsSub"),
        },
        {
            Icon: CitiesIcon,
            value: formatCount(counts?.citiesCovered, t("homePage.platformBanner.stats.citiesValue")),
            label: t("homePage.platformBanner.stats.citiesLabel"),
            sub: t("homePage.platformBanner.stats.citiesSub"),
        },
    ];

    return (
        <div className={styles.fullWidth}>
            <Container className={styles.inner}>
                <div className={styles.left}>
                    <div className={styles.accent} />
                    <h2 className={styles.title}>
                        <span className={styles.titleLine1}>{t("homePage.platformBanner.titleLine1")}</span>
                        <span className={styles.titleLine2}>{t("homePage.platformBanner.titleLine2")}</span>
                    </h2>
                    <a href="#recherche" onClick={scrollToSearch} className={styles.cta}>
                        <span>{t("homePage.platformBanner.cta")}</span>
                        <ArrowForward className={styles.ctaArrow} />
                    </a>
                </div>
                <div className={styles.right}>
                    <div className={styles.quoteBlock}>
                        <FormatQuote className={styles.quoteIcon} />
                        <p className={styles.testimonial}>{t("homePage.platformBanner.testimonial")}</p>
                    </div>
                    <div className={styles.author}>{t("homePage.platformBanner.author")}</div>
                </div>
                <div className={styles.stats}>
                    {stats.map((stat) => (
                        <div key={stat.label} className={styles.stat}>
                            <div className={styles.statIcon}>
                                <stat.Icon />
                            </div>
                            <div className={styles.statText}>
                                <div className={styles.statValue}>{stat.value}</div>
                                <div className={styles.statLabel}>{stat.label}</div>
                                <div className={styles.statSub}>{stat.sub}</div>
                            </div>
                        </div>
                    ))}
                </div>
            </Container>
        </div>
    );
});
